import React from 'react';

import Axios, { CancelTokenSource } from 'axios';

import API from 'utils/api';
import Logger from 'utils/logger';
import { ARCResponse } from 'utils/api/modules/alarms/alarms.types';

export interface ArcsState {
  arcs: ARCResponse[];
  loading: boolean;
  error: boolean;
}

export interface OrganisationArcs extends ArcsState {
  retry?: () => void;
  // Only for admins, who see workers of other organisations.
  loadOrganisationArcs?: (organisationUuid: string) => Promise<ARCResponse[]>;
}

export const LOADING_ARCS: ArcsState = { arcs: [], loading: true, error: false };

// Grid keeps its column definitions from the first render, so cells read the ARC list from context, not a closure.
export const OrganisationArcsContext = React.createContext<OrganisationArcs>(LOADING_ARCS);

export const useOrganisationArcs = (enabled: boolean, canLoadOtherOrganisations: boolean): OrganisationArcs => {
  const [state, setState] = React.useState<ArcsState>(LOADING_ARCS);
  const [attempt, setAttempt] = React.useState(0);
  const organisationRequests = React.useRef(new Map<string, Promise<ARCResponse[]>>());

  React.useEffect(() => {
    if (!enabled) return undefined;
    const cancelTokenSource = Axios.CancelToken.source();
    API.alarms
      .loadArcs({ limit: 1000 }, cancelTokenSource)()
      .then(({ data }) => setState({ arcs: data.results, loading: false, error: false }))
      .catch((error) => {
        if (Axios.isCancel(error)) return;
        Logger.error('Could not load organisation ARCs', error);
        setState({ arcs: [], loading: false, error: true });
      });
    return () => cancelTokenSource.cancel();
  }, [enabled, attempt]);

  const retry = React.useCallback(() => {
    setState(LOADING_ARCS);
    setAttempt((current) => current + 1);
  }, []);

  // Created on first use, not in an effect: the rows' effects run before this page's, so they would find none yet.
  const organisationCancelToken = React.useRef<CancelTokenSource>();
  React.useEffect(
    () => () => {
      organisationCancelToken.current?.cancel();
      organisationCancelToken.current = undefined;
    },
    []
  );

  // One request per organisation, shared by every row of it, so no single row may cancel it; the page does on unmount.
  const loadOrganisationArcs = React.useCallback((organisationUuid: string): Promise<ARCResponse[]> => {
    let request = organisationRequests.current.get(organisationUuid);
    if (!request) {
      organisationCancelToken.current ??= Axios.CancelToken.source();
      request = API.alarms
        .loadArcs({ limit: 1000, organisation: organisationUuid }, organisationCancelToken.current)()
        .then(({ data }) => data.results);
      request.catch((error) => {
        if (!Axios.isCancel(error)) Logger.error("Could not load the worker's organisation ARCs", error);
        organisationRequests.current.delete(organisationUuid);
      });
      organisationRequests.current.set(organisationUuid, request);
    }
    return request;
  }, []);

  return React.useMemo(
    () => ({ ...state, retry, loadOrganisationArcs: canLoadOtherOrganisations ? loadOrganisationArcs : undefined }),
    [state, retry, canLoadOtherOrganisations, loadOrganisationArcs]
  );
};
